import React, { useEffect, useRef } from 'react';

interface OpenDialog {
  contains: (node: Node) => boolean;
  close: () => void;
}

/**
 * The dialogs open now, the last opened on top. Only the top one closes on Escape or on a click outside it, so
 * answering a confirmation asked from a side panel leaves the panel open.
 */
const openDialogs: OpenDialog[] = [];

const topDialog = () => openDialogs[openDialogs.length - 1];

const closeTopOnEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') topDialog()?.close();
};

const closeTopOnClickOutside = (e: MouseEvent) => {
  const top = topDialog();
  if (top && !top.contains(e.target as Node)) top.close();
};

/** While open, the dialog in `contentRef` closes on Escape or a click outside it, unless another dialog is above it */
export function useDismissableDialog(isOpen: boolean, contentRef: React.RefObject<HTMLElement | null>, onClose: () => void) {
  // A new onClose on each render must not move the dialog to the top of the stack
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const dialog: OpenDialog = {
      contains: node => !!contentRef.current?.contains(node),
      close: () => onCloseRef.current(),
    };
    if (openDialogs.length === 0) {
      document.addEventListener('mousedown', closeTopOnClickOutside);
      document.addEventListener('keydown', closeTopOnEscape);
    }
    openDialogs.push(dialog);
    return () => {
      openDialogs.splice(openDialogs.indexOf(dialog), 1);
      if (openDialogs.length === 0) {
        document.removeEventListener('mousedown', closeTopOnClickOutside);
        document.removeEventListener('keydown', closeTopOnEscape);
      }
    };
  }, [isOpen, contentRef]);
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /** A sheet docks to the right of the screen, for editing one item of a list */
  variant?: 'dialog' | 'sheet';
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, className = '', variant = 'dialog' }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useDismissableDialog(isOpen, modalRef, onClose);

  if (!isOpen) return null;

  return (
    <div className={variant === 'sheet' ? 'modal-overlay sheet-overlay' : 'modal-overlay'}>
      <div
        className={`modal-content ${variant === 'sheet' ? 'sheet ' : ''}${className}`.trim()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;
